// project imports
import { hasModuleAccess } from 'utils/roleWeights';

// assets
import {
  IconApprove,
  IconBadge,
  IconBeach,
  IconBriefcase,
  IconClock,
  IconDocSource,
  IconEmploymentPeriod,
  IconFamilyCare,
  IconListAlt,
  IconLocation,
  IconLock,
  IconMedical,
  IconMoneyOff,
  IconPendingActions,
  IconPropustkaSettings,
  IconShieldCheck,
  IconShieldWarning,
  IconTeam,
  IconWarningTriangle
} from 'utils/icons';

// icons
const icons = {
  IconMedical,
  IconMoneyOff,
  IconShieldCheck,
  IconShieldWarning,
  IconApprove,
  IconLocation,
  IconLock,
  IconBriefcase,
  IconTeam,
  IconListAlt,
  IconBeach,
  IconPendingActions,
  IconClock,
  IconPropustkaSettings,
  IconEmploymentPeriod,
  IconBadge,
  IconDocSource,
  IconWarningTriangle,
  IconFamilyCare
};

// ==============================|| MENU ITEMS - DOCHÁZKOVÝ SYSTÉM (SAMOSTATNÁ TOP-LEVEL SKUPINA) ||============================== //
// 2026-09-01, na výslovné přání uživatele - vlastní top-level skupina
// v levém menu, launcher na "Řídicí panel" podle role - sekce oddělené
// "linií uprostřed [role]" (type:'divider', viz Navigation/NavCollapse.jsx),
// každá s jedním nebo více odkazy. Do 2026-09-02 existoval vedle ní ještě
// samostatný buildAttendanceManagerCollapse() (operativní CRUD menu
// vnořené v "Manažeři") - zrušen, jeho odkazy jsou teď součástí sekcí
// níže (Oblastní manažer/Správce).
//
// "Mzdová účetní" gatuje na STEJNÝ 'regional_manager' jako "Oblastní
// manažer" - podle už zdokumentovaného mechanismu (AttendanceGuide.jsx,
// sekce "Přístup k docházce") je mzdová účetní ve skutečnosti táž role
// "oblastní manažer", jen přidělená BEZ scope_group_id omezení na jednu
// pobočku - rozsah (jedna pobočka vs. celá firma) řeší backend, ne menu.
//
// "Správce" > "Technická dokumentace" (2026-09-01, na výslovné přání
// uživatele - "jsem chtěl uplně na novou stránku") NEMÍŘÍ na existující
// /admin/docs/attendance (AdminAttendanceGuide.jsx, popisuje jen skutečně
// postavené části appky) - je to VLASTNÍ nová stránka s vertikálním
// záložkovým menu (pages/attendance/dashboard/TechnicalDocumentation.jsx),
// obsahově jiného druhu (cílový/referenční popis modulu, ne nutně 1:1 s
// aktuálně postaveným chováním).
//
// "Zaměstnanec" > "Dokumentace pro zaměstnance" (2026-09-01) sdílí stejný
// vizuální shell (AccordionDocPage) jako Technická dokumentace/Dokumentace DS,
// ale obsahově je to glosář pojmů popisující SKUTEČNÉ dnešní chování (jako
// AdminAttendanceGuide.jsx), ne cílový stav se stavovými štítky - viz
// pages/attendance/dashboard/EmployeeDocumentation.jsx.
export function buildAttendanceDashboardMenu(user) {
  const isReader = hasModuleAccess(user?.permissions, 'attendance', 'reader');
  const isManager = hasModuleAccess(user?.permissions, 'attendance', 'regional_manager');
  const isAdmin = hasModuleAccess(user?.permissions, 'attendance', 'admin');
  // "Mzdová účetní" (2026-09-01, na výslovné přání uživatele - "do sekce
  // mzdová účetní nemá krom její role a admin další přístup") - NENÍ totéž
  // co isManager. regional_manager samo o sobě vedoucího pobočky od mzdové
  // účetní neodliší (obě drží stejnou roli/váhu) - rozhoduje
  // attendance_payroll_access z /auth/me (AuthController.php,
  // PermissionService::visibleUserIdsFor() - neomezený dosah = mzdová
  // účetní nebo superadmin, scoped = vedoucí pobočky).
  const isPayroll = Boolean(user?.attendance_payroll_access);

  if (!isReader && !isManager && !isAdmin) return null;

  const children = [];

  // "Zaměstnanec" (2026-09-01, na výslovné přání uživatele) - PŘED
  // "Oblastní manažer", gatuje jen na 'reader' (kdokoliv s přístupem k
  // Docházce, ne jen manažeři) - odkazuje na glosář pojmů, ne na
  // gap-analýzu cílového stavu jako Technická dokumentace/Dokumentace DS.
  if (isReader) {
    children.push(
      { id: 'attendance-dashboard-divider-employee', type: 'divider', title: 'Zaměstnanec' },
      {
        // "Žádanka na propustku" (2026-09-01, na výslovné přání uživatele -
        // "pravděpodobně chybí v sekci zaměstnanec") - plánovaný odchod na
        // propustku vyžadující předchozí schválení nadřízeným, viz
        // pages/attendance/PropustkaRequest.jsx.
        id: 'attendance-dashboard-employee-propustka-request-link',
        title: 'Žádost o propustku',
        type: 'item',
        url: '/attendance/propustky/zadanka',
        icon: icons.IconPendingActions,
        breadcrumbs: false
      },
      {
        // "Žádost o dovolenou" (2026-09-04, na výslovné přání uživatele -
        // rozdělení dřívější kombinované stránky s přepínačem typu na dva
        // samostatné odkazy), stejný vzor jako žádanka na propustku výše,
        // viz pages/attendance/LeaveRequestVacation.jsx. Sourozenec o řádek
        // níže: LeaveRequestUnpaid.jsx.
        id: 'attendance-dashboard-employee-leave-request-vacation-link',
        title: 'Žádost o dovolenou',
        type: 'item',
        url: '/attendance/dovolena/zadost',
        icon: icons.IconBeach,
        breadcrumbs: false
      },
      {
        id: 'attendance-dashboard-employee-leave-request-unpaid-link',
        title: 'Žádost o neplacené volno',
        type: 'item',
        url: '/attendance/neplacene-volno/zadost',
        icon: icons.IconMoneyOff,
        breadcrumbs: false
      },
      {
        id: 'attendance-dashboard-employee-docs-link',
        title: 'Dokumentace DS',
        type: 'item',
        url: '/attendance/dashboard/employee-documentation',
        icon: icons.IconDocSource,
        breadcrumbs: false
      }
    );
  }

  if (isManager) {
    children.push(
      { id: 'attendance-dashboard-divider-regional', type: 'divider', title: 'Oblastní manažer' },
      {
        // Přejmenováno z "Řídicí panel" (2026-09-01, na výslovné přání
        // uživatele) - stejný název jako mzdová účetní's "Přehled
        // zaměstnanců" níže (jiná URL/obsah, ale sjednocený název i ikona).
        id: 'attendance-dashboard-regional-link',
        title: 'Přehled zaměstnanců',
        type: 'item',
        url: '/attendance/dashboard/regional-manager',
        icon: icons.IconBadge,
        breadcrumbs: false
      },
      {
        // Přesunuto sem z buildAttendanceManagerCollapse() (Manažeři >
        // Docházkový systém), 2026-09-02 na výslovné přání uživatele -
        // stará samostatná položka v levém menu je zrušená, tenhle launcher
        // je teď jediné místo. Stejná URL/gate jako dřív.
        id: 'attendance-dashboard-regional-monthly-card-link',
        title: 'Docházkové karty',
        type: 'item',
        url: '/attendance',
        icon: icons.IconPendingActions,
        breadcrumbs: false
      },
      {
        id: 'attendance-dashboard-regional-sick-leave-link',
        title: 'Pracovní neschopenka',
        type: 'item',
        url: '/attendance/manager/sick-leave',
        icon: icons.IconMedical,
        breadcrumbs: false
      },
      {
        id: 'attendance-dashboard-regional-family-care-link',
        title: 'Ošetřování člena rodiny',
        type: 'item',
        url: '/attendance/manager/family-care',
        icon: icons.IconFamilyCare,
        breadcrumbs: false
      },
      {
        id: 'attendance-dashboard-regional-obstacle-link',
        title: 'Překážka v práci 60%',
        type: 'item',
        url: '/attendance/manager/obstacle',
        icon: icons.IconShieldWarning,
        breadcrumbs: false
      },
      {
        id: 'attendance-dashboard-regional-approvals-link',
        title: 'Dovolená / N. volno',
        type: 'item',
        url: '/attendance/manager/approvals',
        icon: icons.IconApprove,
        breadcrumbs: false
      },
      {
        id: 'attendance-dashboard-regional-propustka-requests-link',
        title: 'Žádosti o propustky',
        type: 'item',
        url: '/attendance/manager/propustka-requests',
        icon: icons.IconPendingActions,
        breadcrumbs: false
      },
      {
        id: 'attendance-dashboard-regional-monthly-approvals-link',
        title: 'Uzávěrka docházky',
        type: 'item',
        url: '/attendance/manager/monthly-approvals',
        icon: icons.IconLock,
        breadcrumbs: false
      },
      {
        id: 'attendance-dashboard-regional-pending-classification-link',
        title: 'Neplánovaná zdravotní absence',
        type: 'item',
        url: '/attendance/manager/pending-classification',
        icon: icons.IconWarningTriangle,
        breadcrumbs: false
      },
      {
        // "Přehled propustek týmu" (2026-09-04, na výslovné přání uživatele
        // - zaměstnanec svou historii propustek vidí na Docházka, manažer/
        // mzdová účetní žádný ekvivalentní výstup neměli). BEZ
        // requirePayrollGrant, stejně jako "Neplánovaná zdravotní absence"
        // výše - vidí ho i vedoucí pobočky, jen bez náhledu přílohy.
        id: 'attendance-dashboard-regional-team-propustky-link',
        title: 'Přehled propustek',
        type: 'item',
        url: '/attendance/manager/team-propustky',
        icon: icons.IconPropustkaSettings,
        breadcrumbs: false
      },
      {
        // Přesunuto sem ze sekce Správce (2026-09-03, na výslovné přání
        // uživatele - "minimální obsazenost patří pod oblastního
        // manažera"). Stejná URL/gate jako dřív (min. 'regional_manager').
        id: 'attendance-dashboard-regional-job-title-minimums-link',
        title: 'Minimální obsazenost',
        type: 'item',
        url: '/attendance/manager/job-title-minimums',
        icon: icons.IconTeam,
        breadcrumbs: false
      },
      {
        // "Dokumentace" je v každé sekci vždy poslední (2026-09-02, na
        // výslovné přání uživatele - standing pravidlo pro pořadí položek
        // v sekcích tohohle menu).
        id: 'attendance-dashboard-regional-docs-link',
        title: 'Dokumentace DS',
        type: 'item',
        url: '/attendance/dashboard/regional-manager-documentation',
        icon: icons.IconDocSource,
        breadcrumbs: false
      }
    );
  }

  if (isPayroll) {
    children.push(
      { id: 'attendance-dashboard-divider-payroll', type: 'divider', title: 'Mzdová účetní' },
      {
        id: 'attendance-dashboard-payroll-link',
        title: 'Přehled zaměstnanců',
        type: 'item',
        url: '/attendance/dashboard/payroll',
        icon: icons.IconBadge,
        breadcrumbs: false
      },
      {
        // 2026-09-01, na výslovné přání uživatele - přesunuto sem z
        // buildAttendanceManagerCollapse() (Manažeři > Docházkový systém,
        // dřív "Důvody propustky") - kategorie důvodu přímo určuje mzdový
        // dopad (viz AttendancePropustkaWageCategory), takže patří mzdové
        // účetní. Stejná URL, ale gate teď navíc vyžaduje
        // attendance_payroll_access - viz RequireModuleAccess's
        // requirePayrollGrant na odpovídající routě.
        id: 'attendance-dashboard-payroll-propustka-settings-link',
        title: 'Nastavení propustky',
        type: 'item',
        url: '/attendance/manager/propustka-reasons',
        icon: icons.IconPropustkaSettings,
        breadcrumbs: false
      },
      {
        // "Úvazek zaměstnance" (2026-09-01, na výslovné přání uživatele) -
        // přesunuto sem z buildAttendanceManagerCollapse() (Manažeři >
        // Docházkový systém). Stejná URL/gate jako dřív (min.
        // 'regional_manager', BEZ requirePayrollGrant - vedoucí pobočky
        // stránku dál mají v backendovém rozsahu, jen ji nevidí v tomhle
        // launcher menu).
        id: 'attendance-dashboard-payroll-employment-periods-link',
        title: 'Úvazek zaměstnance',
        type: 'item',
        url: '/attendance/manager/employment-periods',
        icon: icons.IconEmploymentPeriod,
        breadcrumbs: false
      },
      {
        // "Nárok na dovolenou" (2026-09-01, na výslovné přání uživatele -
        // "uprav styl a oprávnění jako při nastavení propustky") - přesunuto
        // sem z buildAttendanceManagerCollapse() (Manažeři > Docházkový
        // systém). Stejná URL, ale gate teď navíc vyžaduje
        // attendance_payroll_access - viz RequireModuleAccess's
        // requirePayrollGrant na odpovídající routě, a backend zápis
        // (set()) je od teď omezený na mzdovou účetní (ATT-403-007).
        id: 'attendance-dashboard-payroll-leave-entitlements-link',
        title: 'Nárok na dovolenou',
        type: 'item',
        url: '/attendance/manager/leave-entitlements',
        icon: icons.IconBeach,
        breadcrumbs: false
      },
      {
        // "Měsíční export pro mzdy" (2026-09-01, Dokumentace DS - Mzdová
        // účetní, "Měsíční podklad pro mzdy") - souhrn odpracovaných/
        // jinak strávených hodin za všechny viditelné zaměstnance,
        // requirePayrollGrant stejně jako ostatní mzdové nástroje výše.
        id: 'attendance-dashboard-payroll-export-link',
        title: 'Měsíční export pro mzdy',
        type: 'item',
        url: '/attendance/manager/payroll-export',
        icon: icons.IconListAlt,
        breadcrumbs: false
      },
      {
        // "Dokumentace" je v každé sekci vždy poslední (2026-09-01, na
        // výslovné přání uživatele - standing pravidlo pro pořadí položek
        // v sekcích tohohle menu).
        id: 'attendance-dashboard-payroll-docs-link',
        title: 'Dokumentace DS',
        type: 'item',
        url: '/attendance/dashboard/payroll-documentation',
        icon: icons.IconDocSource,
        breadcrumbs: false
      }
    );
  }

  if (isAdmin) {
    children.push(
      { id: 'attendance-dashboard-divider-admin', type: 'divider', title: 'Správce' },
      {
        // Přesunuto sem z buildAttendanceManagerCollapse() (Manažeři >
        // Docházkový systém), 2026-09-02 na výslovné přání uživatele.
        // Stejná URL/gate (minRole 'regional_manager' na route, viz
        // MainRoutes.jsx) - mění se jen umístění v launcher menu, ne
        // skutečné oprávnění.
        id: 'attendance-dashboard-admin-areas-link',
        title: 'GPS povolené lokace',
        type: 'item',
        url: '/attendance/manager/areas',
        icon: icons.IconLocation,
        breadcrumbs: false
      },
      {
        // Přesunuto sem z buildAttendanceManagerCollapse() (Manažeři >
        // Docházkový systém), 2026-09-02 na výslovné přání uživatele.
        // Stejná URL/gate (minRole 'regional_manager' na route, viz
        // MainRoutes.jsx) - mění se jen umístění v launcher menu, ne
        // skutečné oprávnění.
        id: 'attendance-dashboard-admin-verification-reasons-link',
        title: 'Ověřovací důvody',
        type: 'item',
        url: '/attendance/manager/verification-reasons',
        icon: icons.IconShieldCheck,
        breadcrumbs: false
      },
      {
        id: 'attendance-dashboard-admin-link',
        title: 'Technická dokumentace',
        type: 'item',
        url: '/attendance/dashboard/technical-documentation',
        icon: icons.IconDocSource,
        breadcrumbs: false
      }
    );
  }

  return {
    id: 'attendance-dashboard-group',
    type: 'group',
    children: [
      {
        id: 'attendance-dashboard-collapse',
        title: 'Docházkový systém',
        type: 'collapse',
        icon: icons.IconClock,
        children
      }
    ]
  };
}
